import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PhotoCamera, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { getAllTests, type TestListingRow } from '../../services/testListingService';
import { fetchFieldEvidenceSummary } from '../../services/observationMediaService';
import {
  evidenceKey, requiredEvidenceFields, TEST_CODE_TO_EVIDENCE_TYPE,
} from '../../constants/evidenceFields';
import { isValidationRow } from './testsPageUtils';
import { MyTestsList } from './MyTestsList';

/** Statuses that represent work still on the bench — the default view. */
const ACTIVE_STATUSES = ['New', 'In Progress', 'On Hold', 'Request Review'];
const NEEDS_EVIDENCE = '__needsEvidence';

/**
 * My Tests — the mobile-first pathway from the Dashboard to a test entry page
 * (mockups 2026-07-24, Option N2). Filter chips instead of a grid, tests
 * grouped by work order, one tap to open. "Needs evidence" surfaces every
 * test with required field-evidence photos still missing so a technician can
 * sweep the lab clearing amber pills. Works on desktop too, but the grids on
 * the Tests page remain the desktop tool of choice.
 */
export const MyTestsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<TestListingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('__active');
  const [search, setSearch] = useState('');
  const [evidenceMissing, setEvidenceMissing] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    getAllTests({})
      .then((data) => setRows(data.filter((r) => !isValidationRow(r))))
      .catch(() => { /* page shows empty state */ })
      .finally(() => setLoading(false));
  }, []);

  // Missing-evidence counts for tests whose type has required evidence fields.
  useEffect(() => {
    const mapped = rows.filter((r) => {
      const typeId = r.TestCode ? TEST_CODE_TO_EVIDENCE_TYPE[r.TestCode] : undefined;
      return typeId && requiredEvidenceFields(typeId).length > 0;
    });
    if (mapped.length === 0) { setEvidenceMissing(new Map()); return; }
    let cancelled = false;
    fetchFieldEvidenceSummary(mapped.map((r) => r.WorkOrderDetailKey))
      .then((summary) => {
        if (cancelled) return;
        const present = new Set(summary.map((s) => `${s.WorkOrderDetailKey}|${s.FieldKey}`));
        const missing = new Map<string, number>();
        mapped.forEach((r) => {
          const typeId = TEST_CODE_TO_EVIDENCE_TYPE[r.TestCode as string];
          const count = requiredEvidenceFields(typeId)
            .filter((f) => !present.has(`${r.WorkOrderDetailKey}|${evidenceKey(typeId, f.fieldKey)}`))
            .length;
          if (count > 0) missing.set(r.WorkOrderDetailKey, count);
        });
        setEvidenceMissing(missing);
      })
      .catch(() => { /* pills degrade to hidden */ });
    return () => { cancelled = true; };
  }, [rows]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach((r) => { counts[r.TestStatus] = (counts[r.TestStatus] || 0) + 1; });
    return counts;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (filter === '__active') return ACTIVE_STATUSES.includes(r.TestStatus);
        if (filter === NEEDS_EVIDENCE) return evidenceMissing.has(r.WorkOrderDetailKey);
        if (filter) return r.TestStatus === filter;
        return true;
      })
      .filter((r) => !q
        || [r.TestName, r.SpecimenRef, r.SampleReference, r.LocationName, r.ProjectName, r.WorkOrderId]
          .some((v) => v?.toLowerCase().includes(q)))
      .sort((a, b) => String(b.sysLastModifiedDate).localeCompare(String(a.sysLastModifiedDate)));
  }, [rows, filter, search, evidenceMissing]);

  const openTest = useCallback((row: TestListingRow) => {
    if (row.ProjectId && row.WorkOrderKey && row.WorkOrderDetailKey) {
      navigate(ROUTES.TEST_PARAMETERS.replace(':projectId', row.ProjectId)
        .replace(':workOrderId', row.WorkOrderKey)
        .replace(':workOrderDetailKey', row.WorkOrderDetailKey));
    }
  }, [navigate]);

  const chip = (label: string, value: string, count: number, color?: string) => (
    <Chip
      key={value}
      label={count > 0 ? `${label} · ${count}` : label}
      size="small"
      onClick={() => setFilter(value)}
      icon={value === NEEDS_EVIDENCE ? <PhotoCamera sx={{ fontSize: '13px !important' }} /> : undefined}
      sx={{
        height: 26, fontSize: '0.7rem', fontWeight: 650, cursor: 'pointer',
        backgroundColor: filter === value ? (color ?? '#4A6741') : alpha(color ?? '#4A6741', 0.08),
        color: filter === value ? '#fff' : (color ?? '#4A6741'),
        '& .MuiChip-icon': { color: 'inherit' },
        '&:hover': { backgroundColor: filter === value ? (color ?? '#4A6741') : alpha(color ?? '#4A6741', 0.16) },
      }}
    />
  );

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', pb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', mb: 1 }}>My Tests</Typography>

      <TextField
        fullWidth size="small" placeholder="Search test, sample, project, WO…"
        value={search} onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
        sx={{ mb: 1.25, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }}
      />

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
        {chip('Active', '__active', ACTIVE_STATUSES.reduce((n, s) => n + (statusCounts[s] || 0), 0))}
        {chip('Needs evidence', NEEDS_EVIDENCE, evidenceMissing.size, '#B26A00')}
        {chip('All', '', rows.length)}
        {Object.entries(statusCounts)
          .filter(([status]) => status && status !== 'Delete')
          .sort()
          .map(([status, count]) => chip(status, status, count))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
          No tests match this filter.
        </Typography>
      ) : (
        <MyTestsList rows={filtered} evidenceMissing={evidenceMissing} onOpen={openTest} />
      )}
    </Box>
  );
};
